// src/layouts/CustomMenu.tsx
//
// 3c Hybrid rail (hybrid-3c.md §4): a light neutral surface
// (palette.background.menu), ink-scale items, one entry per destination (the
// destination's pages are reached through the section bar under the app bar,
// §5.1 — see menuStructure.ts), a hairline between the business and platform
// blocks, the two-tone brand lockup at the top, and a 3px teal marker on the
// active entry. A destination with no page visible to the current user is not
// shown. Responsive pattern unchanged: an independent `sm` breakpoint check
// renders the panel at full width on mobile, and clicking an entry
// auto-closes it.
import React from 'react';
import { useTranslate, useSidebarState, useGetIdentity } from 'react-admin';
import { Box, Typography, useMediaQuery, MenuItem as MuiMenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

import { ProfileRailCard } from './ProfileRailCard';
import { useSectionTabs } from './useSectionTabs';

interface StyledMenuItemProps {
  open: boolean;
}

const StyledMenuItem = styled(MuiMenuItem, {
    shouldForwardProp: (prop) => prop !== 'open',
})<StyledMenuItemProps>(({ theme, open }) => ({
    position: 'relative',
    margin: theme.custom.menu.item.margin,
    padding: theme.custom.menu.item.padding,
    borderRadius: theme.custom.menu.item.borderRadius,
    color: theme.custom.menu.item.color(theme),
    justifyContent: open ? 'flex-start' : 'center',
    // Row height is fixed here — MuiMenuItem's 48px default minHeight would
    // otherwise stretch it (hybrid-3c.md §4 "Entry row height 36px fixed").
    minHeight: theme.custom.menu.item.minHeight,
    fontSize: theme.custom.menu.item.fontSize,
    '& .MuiListItemText-root': {
        minWidth: 0,
    },
    '& .MuiListItemText-primary': {
        fontSize: theme.custom.menu.item.fontSize,
        lineHeight: theme.custom.menu.item.lineHeight,
        // Weight 600 lives on the active entry only (hybrid-3c.md §6) — rest is 500.
        fontWeight: 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },

    display: 'flex',
    gap: theme.custom.menu.item.gap,
    transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',

    '& .MuiListItemIcon-root': {
        minWidth: 'auto',
        color: theme.custom.menu.item.iconColor(theme),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: theme.custom.menu.item.iconSize,
        '& .MuiSvgIcon-root': {
            fontSize: theme.custom.menu.item.iconSize,
        },
    },

    '&:hover:not(.active)': {
        color: theme.custom.menu.item.hover.color(theme),
        backgroundColor: open ? theme.custom.menu.item.hover.backgroundColor(theme) : 'transparent',
        ...(!open && {
            '& .MuiListItemIcon-root': {
                backgroundColor: theme.custom.menu.item.hover.backgroundColor(theme),
            },
        }),
    },

    '&.active': {
        color: theme.custom.menu.item.active.color(theme),
        backgroundColor: open ? theme.custom.menu.item.active.backgroundColor(theme) : 'transparent',
        '& .MuiListItemText-primary': {
            fontWeight: theme.custom.menu.item.active.fontWeight,
        },

        // Leading-edge marker — the component skips the pseudo-element
        // entirely when the token is 'transparent', so a product can turn
        // the bar off without touching this file.
        '&::before': open && theme.custom.menu.item.active.marker(theme) !== 'transparent'
            ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: theme.custom.menu.item.active.markerInset,
                  bottom: theme.custom.menu.item.active.markerInset,
                  width: theme.custom.menu.item.active.markerWidth,
                  borderRadius: theme.custom.menu.item.active.markerRadius,
                  backgroundColor: theme.custom.menu.item.active.marker(theme),
              }
            : undefined,

        '& .MuiListItemIcon-root': {
            color: open ? theme.custom.menu.item.active.marker(theme) : theme.custom.menu.item.closedActiveIcon.color(theme),
            backgroundColor: open ? 'transparent' : theme.custom.menu.item.closedActiveIcon.backgroundColor(theme),
            padding: open ? 0 : '6px',
            borderRadius: open ? 0 : (theme.custom.menu.item.closedActiveIcon.borderRadius || '6px'),
        },

        '&:hover': {
            backgroundColor: open ? theme.custom.menu.item.active.hover.backgroundColor(theme) : 'transparent',
        },
    },
}));

export const CustomMenu: React.FC = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const navigate = useNavigate();
    const [isSidebarOpen, setSidebarOpen] = useSidebarState();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // The rail waits for the identity like the rest of the shell does.
    const { isLoading } = useGetIdentity();
    const { groups, group: activeGroup } = useSectionTabs();

    const handleMenuClick = (to: string): void => {
        navigate(to);
        if (isSmallScreen) {
            setSidebarOpen(false);
        }
    };

    const railSx = {
        display: 'flex',
        flexDirection: 'column',
        // Patrón A: el rail ocupa TODA la altura del viewport, a ras de los bordes
        // superior e inferior. La AppBar es absoluta sobre la columna de contenido
        // (ver CustomAppBar/CustomLayout), así que aquí no hay franja de app-bar ni
        // margen exterior que restar.
        height: '100vh',
        boxSizing: 'border-box',
        backgroundColor: (theme.palette.background as any).menu || theme.palette.background.paper,
        borderRight: isSmallScreen ? 'none' : `1px solid ${theme.custom.menu.border}`,
        width: isSmallScreen ? '94%' : (isSidebarOpen ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth),
        minWidth: isSmallScreen ? 'auto' : (isSidebarOpen ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth),
        maxWidth: isSmallScreen ? '100%' : (isSidebarOpen ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth),
        transition: 'width 0.2s ease-in-out, min-width 0.2s ease-in-out, max-width 0.2s ease-in-out, padding 0.2s ease-in-out',
        overflow: 'hidden',
    } as const;

    if (isLoading) {
        return (
            <Box sx={{ ...railSx, padding: '12px', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ color: theme.custom.text.tertiary }}>
                    {translate('ra.action.loading')}
                </Box>
            </Box>
        );
    }

    return (
        // Symmetric container padding (hybrid-3c.md §4 "Keep the container
        // padding symmetric"): 16px 10px open / 16px 6px closed / 12px mobile.
        <Box sx={{ ...railSx, padding: isSmallScreen ? '12px' : (isSidebarOpen ? '16px 10px' : '16px 6px') }}>
            {(isSidebarOpen || isSmallScreen) && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', padding: '4px 8px 20px' }}>
                    {/* Two-tone brand lockup, once, at the top of the rail — the
                        product name appears only here (no logotype in-app, no
                        app-bar wordmark). hybrid-3c.md §4 "Brand lockup". */}
                    <Typography
                        component="div"
                        noWrap
                        sx={{ fontSize: '1.15rem', lineHeight: 1.2, letterSpacing: '-0.01em' }}
                    >
                        <Box component="span" sx={{ color: theme.custom.menu.brand.color, fontWeight: 800 }}>
                            Roommatik
                        </Box>
                        <Box component="span" sx={{ color: theme.custom.menu.brand.mutedColor, fontWeight: 400 }}>
                            {' '}Insight
                        </Box>
                    </Typography>
                </Box>
            )}

            <Box
                sx={{
                    flex: 1,
                    overflowY: isSidebarOpen || isSmallScreen ? 'auto' : 'hidden',
                    overflowX: 'hidden',
                    // Thin, idle-hidden scrollbar (hybrid-3c.md §4 "Rail
                    // scrollbar") — the gutter stays reserved either way, so
                    // revealing the thumb on hover shifts nothing.
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: 'transparent', borderRadius: '999px' },
                    '&:hover::-webkit-scrollbar-thumb': { backgroundColor: theme.custom.menu.scrollbarThumb },
                    '&::-webkit-scrollbar-thumb:hover': { backgroundColor: theme.custom.menu.scrollbarThumbHover },
                }}
            >
                {groups.map((group, index) => (
                    <React.Fragment key={group.key}>
                        {/* Hairline between the business and platform blocks
                            (hybrid-3c.md §4 "Group separator"), never above the
                            first entry. */}
                        {index > 0 && group.block !== groups[index - 1].block && (
                            <Box
                                role="separator"
                                sx={{ borderTop: `1px solid ${theme.custom.menu.separatorColor}`, margin: '10px 8px' }}
                            />
                        )}
                        {/* An entry opens the destination's first visible page. */}
                        <StyledMenuItem
                            onClick={() => handleMenuClick(group.tabs[0].to)}
                            open={isSidebarOpen}
                            className={activeGroup?.key === group.key ? 'active' : ''}
                            aria-current={activeGroup?.key === group.key ? 'page' : undefined}
                            aria-label={translate(group.labelKey, { smart_count: 2 })}
                        >
                            <ListItemIcon>{group.icon}</ListItemIcon>
                            {(isSidebarOpen || isSmallScreen) && (
                                <ListItemText
                                    primary={translate(group.labelKey, { smart_count: 2 })}
                                    primaryTypographyProps={{ noWrap: true }}
                                />
                            )}
                        </StyledMenuItem>
                    </React.Fragment>
                ))}
            </Box>

            {/* v2 rule: profile card anchors the bottom of the rail (replaces
                the app-bar user menu — a single place for the profile). */}
            {/* Rendered open or collapsed (hybrid-3c.md §4 "Collapsed: avatar
                only, centred") — the component itself reads the sidebar state. */}
            <ProfileRailCard />
        </Box>
    );
};

export default CustomMenu;
